import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Key, LockKey, LockKeyOpen, ShieldCheck, Prohibit, Play, ArrowsClockwise,
  Eye, EyeSlash, Lock, Warning, Lightning, SealCheck,
} from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";

const LEVELS = {
  L0: {
    label: "L0 · Vendor-managed",
    chip: "bg-slate-100 text-slate-700 border-slate-300",
    blurb: "Platform holds the key. Standard Fernet/AES at rest. No customer key separation.",
  },
  L2: {
    label: "L2 · Customer-managed (CMEK/BYOK)",
    chip: "bg-emerald-50 text-emerald-700 border-emerald-300",
    blurb: "A per-tenant data key is wrapped by the customer's own CMK. Revoke the key and the data goes dark — even to us.",
  },
  L3: {
    label: "L3 · Client-side sealed (zero-knowledge)",
    chip: "bg-indigo-50 text-indigo-700 border-indigo-300",
    blurb: "Crown-jewel fields are sealed in the client. The server stores ciphertext it can never decrypt.",
  },
};

function LevelChip({ level, testid }) {
  const l = LEVELS[level] || LEVELS.L0;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold border rounded-sm ${l.chip}`} data-testid={testid}>
      {level === "L0" ? <LockKeyOpen size={13} weight="bold" /> : <LockKey size={13} weight="bold" />}
      {l.label}
    </span>
  );
}

export default function ConsoleKeySecurity() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await api.get("/console/tenants");
      const ts = data?.tenants || [];
      setTenants(ts);
      setTenantId((cur) => cur || ts[0]?.id || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load tenants");
    }
  }, []);

  const loadStatus = useCallback(async () => {
    if (!tenantId) { setStatus(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/bcsi/tenants/${tenantId}/status`);
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load key status");
      setStatus(null);
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const tenantName = useMemo(
    () => tenants.find((t) => t.id === tenantId)?.name || tenantId,
    [tenants, tenantId]);

  const act = async (verb, label) => {
    setBusy(verb);
    try {
      await api.post(`/bcsi/tenants/${tenantId}/${verb}`);
      toast.success(label);
      await loadStatus();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(""); }
  };

  const connectKey = async () => {
    setBusy("connect");
    try {
      const { data } = await api.post(`/bcsi/tenants/${tenantId}/connect-key`, { provider: "local" });
      toast.success(`Customer key connected — ${tenantName} is now at L2 (${data.cmk_ref})`);
      await loadStatus();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Connect failed");
    } finally { setBusy(""); }
  };

  const level = status?.level || "L0";
  const connected = !!status?.key_connected;
  const enabled = status?.key_enabled;

  return (
    <div className="space-y-6" data-testid="console-key-security">
      <div className="border-b border-border pb-4">
        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
          <Key size={20} weight="duotone" /> BCSI Key Security — Cryptographic Separation
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Manage per-tenant encryption keys for BCSI data on the shared platform. Connect a customer-managed
          key (CMEK/BYOK) to move a tenant from L0 → L2, then prove the model with the live demo below.
          <span className="ml-1 inline-flex items-center gap-1 text-amber-700"><Warning size={13} weight="fill" />
            Keys are backed by a local simulated KMS in this environment.</span>
        </p>
      </div>

      {/* Tenant selector */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-80 max-w-full">
          <Label className="text-xs">Tenant / entity</Label>
          <Select value={tenantId} onValueChange={setTenantId}>
            <SelectTrigger className="rounded-none" data-testid="keysec-tenant-select"><SelectValue placeholder="Select a tenant" /></SelectTrigger>
            <SelectContent>
              {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" className="rounded-none" onClick={loadStatus} disabled={loading} data-testid="keysec-refresh">
          <ArrowsClockwise size={15} className="mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-muted-foreground" data-testid="keysec-loading">Loading key status…</div>
      ) : !status ? (
        <div className="p-6 text-sm text-muted-foreground">Select a tenant to view key status.</div>
      ) : (
        <>
          {/* Status card */}
          <div className="border border-border bg-card p-5 space-y-4" data-testid="keysec-status-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Current protection level</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <LevelChip level={level} testid="keysec-level-chip" />
                  {connected && (
                    enabled
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium" data-testid="keysec-key-enabled"><ShieldCheck size={13} weight="fill" /> Key active</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-red-700 font-medium" data-testid="keysec-key-dark"><Prohibit size={13} weight="fill" /> Key disabled — data is dark</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2 max-w-2xl">{(LEVELS[level] || LEVELS.L0).blurb}</p>
              </div>
              <div className="text-xs text-muted-foreground text-right">
                <div>Provider: <span className="font-mono text-foreground">{status.provider || "—"}</span></div>
                {status.dek_version != null && <div>DEK version: <span className="font-mono text-foreground">v{status.dek_version}</span></div>}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              {!connected && (
                <Button className="rounded-none bg-primary hover:bg-primary/90" onClick={connectKey} disabled={busy === "connect"} data-testid="keysec-connect-btn">
                  <LockKey size={15} className="mr-1" /> {busy === "connect" ? "Connecting…" : "Connect customer key (→ L2)"}
                </Button>
              )}
              {connected && enabled && (
                <Button variant="outline" className="rounded-none text-red-700 border-red-300 hover:bg-red-50" onClick={() => act("revoke", "Key disabled — tenant data is now cryptographically dark")} disabled={busy === "revoke"} data-testid="keysec-revoke-btn">
                  <Prohibit size={15} className="mr-1" /> {busy === "revoke" ? "Revoking…" : "Revoke key (go dark)"}
                </Button>
              )}
              {connected && !enabled && (
                <Button variant="outline" className="rounded-none text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => act("enable", "Key re-enabled — data restored")} disabled={busy === "enable"} data-testid="keysec-enable-btn">
                  <Play size={15} className="mr-1" /> {busy === "enable" ? "Enabling…" : "Re-enable key"}
                </Button>
              )}
            </div>
          </div>

          <DemoPanel tenantId={tenantId} status={status} />
        </>
      )}
    </div>
  );
}

function DemoPanel({ tenantId, status }) {
  const [field, setField] = useState("substation_access_code");
  const [value, setValue] = useState("");
  const [sealed, setSealed] = useState(false);
  const [writing, setWriting] = useState(false);
  const [secrets, setSecrets] = useState([]); // {id, field, sealed}
  const connected = !!status?.key_connected;
  const enabled = status?.key_enabled;

  const write = async () => {
    if (!value.trim()) { toast.error("Enter a value to encrypt"); return; }
    setWriting(true);
    try {
      const { data } = await api.post(`/bcsi/tenants/${tenantId}/demo/secret`, {
        field: field || "demo_field", value, sealed,
      });
      setSecrets((s) => [{ id: data.id, field: field || "demo_field", sealed: data.sealed }, ...s]);
      toast.success(sealed ? "Sealed (L3) value stored verbatim as client ciphertext" : "Value encrypted & stored as ciphertext");
      setValue("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Write failed");
    } finally { setWriting(false); }
  };

  if (!connected) {
    return (
      <div className="border border-dashed border-border p-5 text-sm text-muted-foreground" data-testid="keysec-demo-locked">
        Connect a customer key above to unlock the live encryption demo (L2/L3).
      </div>
    );
  }

  return (
    <div className="border border-border" data-testid="keysec-demo-panel">
      <div className="px-4 py-3 border-b border-border bg-muted flex items-center gap-2">
        <Lightning size={16} weight="duotone" />
        <h3 className="text-sm font-semibold text-foreground">Live encryption demo</h3>
      </div>
      <div className="p-4 space-y-4 pb-20">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Write a value, then read it back to see the ciphertext-at-rest versus the decrypted plaintext.
          Toggle <b>Sealed (L3)</b> to prove the server can never decrypt a client-sealed field. Revoke the
          key in the status card above and the L2 read will return “data is dark”.
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Field name</Label>
            <Input value={field} onChange={(e) => setField(e.target.value)} className="rounded-none font-mono text-xs" data-testid="keysec-field-input" />
          </div>
          <div>
            <Label className="text-xs">Secret value (BCSI)</Label>
            <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. Gate-7 code 4821" className="rounded-none" data-testid="keysec-value-input" />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={sealed} onCheckedChange={setSealed} data-testid="keysec-sealed-switch" />
            <span className="text-xs font-medium flex items-center gap-1">
              <SealCheck size={14} weight="duotone" /> Seal in client (L3 — zero-knowledge)
            </span>
          </div>
          <Button className="rounded-none" onClick={write} disabled={writing} data-testid="keysec-write-btn">
            <Lock size={15} className="mr-1" /> {writing ? "Encrypting…" : "Encrypt & store"}
          </Button>
        </div>

        {sealed && (
          <p className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1.5">
            Placeholder L3: the value is passed through as client ciphertext for this increment. Real browser-side
            WebCrypto sealing lands in the next increment.
          </p>
        )}

        <div className="space-y-2" data-testid="keysec-secret-list">
          {secrets.length === 0 && <div className="text-xs text-muted-foreground">No demo values yet.</div>}
          {secrets.map((s) => (
            <SecretRow key={s.id} tenantId={tenantId} secret={s} keyEnabled={enabled} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SecretRow({ tenantId, secret, keyEnabled }) {
  const [reveal, setReveal] = useState(null); // {mode:'cipher'|'plain', text}
  const [busy, setBusy] = useState("");

  const read = async (showCipher) => {
    setBusy(showCipher ? "cipher" : "plain");
    try {
      const { data } = await api.get(`/bcsi/tenants/${tenantId}/demo/secret/${secret.id}`, {
        params: { show_ciphertext: showCipher },
      });
      if (showCipher) {
        setReveal({ mode: "cipher", text: JSON.stringify(data.ciphertext_at_rest, null, 2) });
      } else {
        setReveal({ mode: "plain", text: data.value });
      }
    } catch (e) {
      const st = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail) || "Read failed";
      if (st === 403) setReveal({ mode: "blocked", text: detail });
      else if (st === 503) setReveal({ mode: "dark", text: detail });
      else toast.error(detail);
    } finally { setBusy(""); }
  };

  return (
    <div className="border border-border p-3 text-sm" data-testid={`keysec-secret-${secret.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-foreground">{secret.field}</span>
          {secret.sealed
            ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-indigo-300 bg-indigo-50 text-indigo-700 rounded-sm"><SealCheck size={11} weight="fill" /> Sealed L3</span>
            : <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-sm"><LockKey size={11} weight="fill" /> L2</span>}
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => read(true)} disabled={!!busy} data-testid={`keysec-read-cipher-${secret.id}`}>
            <EyeSlash size={12} className="mr-1" /> {busy === "cipher" ? "…" : "Ciphertext at rest"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => read(false)} disabled={!!busy} data-testid={`keysec-read-plain-${secret.id}`}>
            <Eye size={12} className="mr-1" /> {busy === "plain" ? "…" : "Decrypt (plaintext)"}
          </Button>
        </div>
      </div>
      {reveal && (
        <div className="mt-2" data-testid={`keysec-reveal-${secret.id}`}>
          {reveal.mode === "cipher" && (
            <pre className="text-[10px] bg-slate-900 text-emerald-300 p-2 overflow-x-auto whitespace-pre-wrap break-all">{reveal.text}</pre>
          )}
          {reveal.mode === "plain" && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-1.5 font-mono break-all">{reveal.text}</div>
          )}
          {reveal.mode === "blocked" && (
            <div className="text-xs bg-indigo-50 border border-indigo-200 text-indigo-800 px-2 py-1.5 flex items-start gap-1.5"><Lock size={13} className="mt-0.5 shrink-0" /> {reveal.text}</div>
          )}
          {reveal.mode === "dark" && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-800 px-2 py-1.5 flex items-start gap-1.5"><Prohibit size={13} className="mt-0.5 shrink-0" /> {reveal.text}</div>
          )}
          {!keyEnabled && reveal.mode !== "cipher" && reveal.mode !== "dark" && (
            <div className="text-[11px] text-red-600 mt-1">Note: the key is currently disabled — decryption will fail until re-enabled.</div>
          )}
        </div>
      )}
    </div>
  );
}
